import { InjectConfig, TaxConfigKey } from '@ddt-web/config-layer';
import * as dayjs from 'dayjs';
import * as timezone from 'dayjs/plugin/timezone';
import * as utc from 'dayjs/plugin/utc';
import * as cloneDeep from 'lodash/cloneDeep';

dayjs.extend(timezone);
dayjs.extend(utc);

export class TaxDataUtil {
	@InjectConfig(TaxConfigKey.ESTIMATES_LOOK_BACK_PERIOD) private static readonly estimatesLookBackPeriod: number = 7;
	/**
	 * Sanitises the taxesMap and substitutionsDetails of the securities we get as part of the resultMap.
	 * The sanitisation replaces negative values with null and removes substitutionDetails based on the provided substitutionTickersToIgnore.
	 *
	 * Negative values are loaded to fix override of previous values for tax with undefined, same for loading a specific holding (eg CASH-USD) as a substitution.
	 * (eg updating substitutions, start of end of year tax season for securities that had mid year estimates as well)
	 *
	 * @param resultMap tax resultMap
	 * @param substitutionTickerToIgnore tickers used to remove substitutionDetail
	 * @returns valid resultMap
	 */
	public static getValidResultMap(resultMap: any, substitutionTickersToIgnore?: string[]): any {
		resultMap?.SECURITY?.forEach(security => {
			if (security.taxesMap) {
				security.taxesMap = TaxDataUtil.sanitiseTaxMap(cloneDeep(security.taxesMap));
			}

			if (security.substitutionsDetails && substitutionTickersToIgnore?.length) {
				security.substitutionsDetails = TaxDataUtil.sanitiseTaxSubstitutions(cloneDeep(security.substitutionsDetails), substitutionTickersToIgnore);
			}
		});

		return resultMap;
	}

	private static sanitiseTaxMap(taxesMap: any): any {
		Object.keys(taxesMap).forEach(key => {
			if (taxesMap[key] < 0) {
				taxesMap[key] = undefined; // replacing with undefined as Number(null) returns 0
			}
		});

		return taxesMap;
	}

	private static sanitiseTaxSubstitutions(substitutionsDetails: any, substitutionTickerToIgnore: string[]): any {
		Object.keys(substitutionsDetails).forEach(key => {
			if (substitutionTickerToIgnore.includes(substitutionsDetails[key]?.ticker)) {
				delete substitutionsDetails[key];
			}
		});

		return substitutionsDetails;
	}

	public static calculateDaysLeftSell(taxEstimatesSellDateTime: string): number {
		const taxEstimatesSellDate: string = taxEstimatesSellDateTime.split('T')[0];
		const currentDate = dayjs().startOf('day');
		return dayjs(taxEstimatesSellDate).add(1, 'day').diff(currentDate, 'day');
	}

	public static hasNewEstimates(daysLeftSell: number, taxEstimatesEntryDateTime: string): boolean {
		const taxEstimatesEntryDate: string = taxEstimatesEntryDateTime.split('T')[0];
		const currentDate = dayjs().startOf('day');
		const daysForNewBadge = dayjs(currentDate).diff(taxEstimatesEntryDate, 'day');
		return daysForNewBadge <= this.estimatesLookBackPeriod && daysLeftSell > 0;
	}
}
